import { useEffect, useMemo, useState } from "react";
import api, { resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Trophy, Plus, Video, Heart, Clock, X, Loader2, Award } from "lucide-react";
import { toast } from "sonner";
import { tr } from "@/lib/translate";
import FileUploader from "@/components/FileUploader";
import ReportMenu from "@/components/ReportMenu";

export default function ChallengesPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [tracks, setTracks] = useState([]);

  const load = async () => {
    const { data } = await api.get("/challenges");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (user?.role === "artist") {
      api.get(`/tracks?artist_id=${user.id}`).then((r) => setTracks(r.data));
    }
  }, [user]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <header className="anim-in mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#FF0050] font-bold flex items-center gap-2">
            <Trophy className="w-4 h-4" /> {t.challenges.title}
          </p>
          <h1 className="text-3xl sm:text-4xl font-black font-display text-white">{t.challenges.title}</h1>
          <p className="text-zinc-400 text-sm mt-1 max-w-xl">{t.challenges.sub}</p>
        </div>
        {user?.role === "artist" && (
          <button onClick={() => setShowCreate(true)} className="btn-primary inline-flex items-center gap-2" data-testid="create-challenge-btn">
            <Plus className="w-4 h-4" /> {t.challenges.create}
          </button>
        )}
      </header>

      {items.length === 0 ? (
        <p className="text-center text-zinc-500 py-12">{t.challenges.empty}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="challenges-grid">
          {items.map((c) => <ChallengeCard key={c.id} c={c} onOpen={() => setActive(c)} t={t} />)}
        </div>
      )}

      {active && <ChallengeRoom challenge={active} onClose={() => { setActive(null); load(); }} t={t} user={user} />}
      {showCreate && <CreateChallengeModal tracks={tracks} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function timeLeft(ends_at) {
  const diff = new Date(ends_at).getTime() - Date.now();
  if (diff <= 0) return "00:00";
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  return days > 0 ? `${days}j ${hours}h` : `${hours}h`;
}

function ChallengeCard({ c, onOpen, t }) {
  const isLive = c.status === "active";
  return (
    <button onClick={onOpen} className="card-interactive overflow-hidden text-left" data-testid={`challenge-${c.id}`}>
      <div className="relative aspect-video">
        <img src={resolveImg(c.track_cover)} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        {isLive ? (
          <span className="absolute top-3 left-3 chip bg-emerald-500 text-white inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {timeLeft(c.ends_at)}
          </span>
        ) : (
          <span className="absolute top-3 left-3 chip bg-zinc-700 text-white">{t.challenges.ended}</span>
        )}
        <span className="absolute top-3 right-3 chip bg-black/60 border border-white/10 text-white inline-flex items-center gap-1">
          <Trophy className="w-3 h-3" /> {Intl.NumberFormat().format(c.prize_amount)} {c.prize_currency}
        </span>
        <div className="absolute bottom-3 left-3 right-3">
          <p className="text-lg font-black font-display text-white drop-shadow truncate">{c.title}</p>
          <p className="text-xs text-zinc-300 truncate">by {c.artist_name} · {c.track_title}</p>
        </div>
      </div>
      <div className="p-3 flex items-center justify-between text-xs">
        <span className="text-zinc-400">{c.submissions.length} {t.challenges.submissions}</span>
        {c.winner && <span className="chip bg-yellow-500/10 text-yellow-300 border border-yellow-500/20 inline-flex items-center gap-1"><Award className="w-3 h-3" />{t.challenges.winner}</span>}
      </div>
    </button>
  );
}

function ChallengeRoom({ challenge, onClose, t, user }) {
  const [c, setC] = useState(challenge);
  const [videoUrl, setVideoUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const { data } = await api.get(`/challenges/${c.id}`);
    setC(data);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!videoUrl.trim()) return;
    setBusy(true);
    try {
      await api.post(`/challenges/${c.id}/submissions`, { video_url: videoUrl.trim(), caption: caption.trim() });
      toast.success(tr("Posted!"));
      setVideoUrl(""); setCaption("");
      refresh();
    } catch (e) {
      toast.error(tr(e.response?.data?.detail || "Could not publish"));
    } finally { setBusy(false); }
  };

  const likeSubmission = async (id) => {
    if (!user) return toast.error(tr("Sign in to interact"));
    try { await api.post(`/submissions/${id}/like`); refresh(); }
    catch { toast.error(tr("Could not update like")); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/95 backdrop-blur-xl overflow-y-auto" data-testid="challenge-room">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <button onClick={onClose} className="mb-4 p-2 rounded-full bg-white/5 hover:bg-white/10 text-white" data-testid="challenge-close">
          <X className="w-4 h-4" />
        </button>
        <div className="card-flat overflow-hidden">
          <div className="relative aspect-video">
            <img src={resolveImg(c.track_cover)} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <span className="absolute top-3 left-3 chip bg-emerald-500 text-white inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {timeLeft(c.ends_at)}
            </span>
            <span className="absolute top-3 right-3 chip bg-yellow-500/90 text-black font-black inline-flex items-center gap-1">
              <Trophy className="w-3 h-3" /> {Intl.NumberFormat().format(c.prize_amount)} {c.prize_currency}
            </span>
            <div className="absolute bottom-3 left-3 right-3">
              <p className="text-xl sm:text-2xl font-black font-display text-white">{c.title}</p>
              <p className="text-sm text-zinc-300">{c.artist_name} · {c.track_title}</p>
            </div>
          </div>
          {c.description && <p className="p-4 text-zinc-300 text-sm">{c.description}</p>}
          {c.announcement_video && (
            <div className="p-4 border-t border-white/5">
              <p className="text-xs uppercase tracking-wider text-zinc-500 font-bold mb-2">{t.challenges.announce}</p>
              <video controls src={c.announcement_video} className="w-full rounded-xl bg-black" />
            </div>
          )}
        </div>

        {/* Submission form */}
        {c.status === "active" && user && (
          <form onSubmit={submit} className="card-flat p-4 mt-4 space-y-3" data-testid="submission-form">
            <p className="text-sm font-semibold text-white">{t.challenges.submitTitle}</p>
            <FileUploader
              accept="video/mp4,video/webm,video/quicktime,video/*"
              label={t.challenges.uploadVideo}
              dataTestid="sub-video-upload"
              preview={videoUrl}
              onUploaded={setVideoUrl}
              onClear={() => setVideoUrl("")}
            />
            <input className="input-base" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder={t.challenges.captionPh} data-testid="sub-caption-input" />
            <button type="submit" disabled={busy || !videoUrl} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="sub-submit-btn">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}<Video className="w-4 h-4" />{t.challenges.submit}
            </button>
          </form>
        )}

        {/* Leaderboard */}
        <h3 className="mt-6 mb-3 text-lg font-black font-display text-white flex items-center gap-2">
          <Trophy className="w-4 h-4 text-yellow-400" /> {t.challenges.leaderboard}
        </h3>
        <p className="text-[11px] text-zinc-500 mb-3" data-testid="prize-disclaimer">
          Le prix est annoncé et offert par l'artiste : Metazik n'intervient pas dans son versement.
        </p>
        {c.submissions.length === 0 ? (
          <p className="text-zinc-500 text-center py-8">{t.challenges.noSubmissions}</p>
        ) : (
          <div className="space-y-2" data-testid="leaderboard">
            {c.submissions.map((s, i) => (
              <div key={s.id} className={`card-flat p-3 flex items-center gap-3 ${i === 0 ? "border-yellow-500/30 bg-yellow-500/5" : ""}`} data-testid={`sub-${s.id}`}>
                <span className="w-7 h-7 grid place-items-center rounded-full bg-zinc-800 text-white font-bold text-xs">{i + 1}</span>
                {i === 0 && <Trophy className="w-4 h-4 text-yellow-400" />}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold truncate">{s.user_name}</p>
                  {s.caption && <p className="text-xs text-zinc-400 truncate">{s.caption}</p>}
                  <a href={resolveImg(s.video_url)} target="_blank" rel="noopener noreferrer" className="text-xs text-[#00F2FE] hover:underline truncate inline-block max-w-full">{s.video_url}</a>
                </div>
                <ReportMenu targetType="submission" targetId={s.id} authorId={s.user_id} authorName={s.user_name} />
                <button onClick={() => likeSubmission(s.id)} className="flex items-center gap-1 px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 transition" data-testid={`sub-like-${s.id}`}>
                  <Heart className="w-4 h-4 text-[#FF0050]" />
                  <span className="text-sm text-white tabular-nums">{s.likes}</span>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CreateChallengeModal({ tracks, onClose, onCreated }) {
  const { t } = useI18n();
  const [d, setD] = useState({ title: "", description: "", track_id: tracks[0]?.id || "", prize_amount: 100000, prize_currency: "FCFA", duration_days: 7, announcement_video: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/challenges", { ...d, prize_amount: parseFloat(d.prize_amount), duration_days: parseInt(d.duration_days, 10), announcement_video: d.announcement_video || null });
      toast.success(tr("Posted!"));
      onCreated();
    } catch (e) {
      toast.error(tr(e.response?.data?.detail || "Could not publish"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-md grid place-items-center p-4" data-testid="create-challenge-modal">
      <div className="w-full max-w-md bg-zinc-950 border border-white/10 rounded-3xl p-6 anim-in relative max-h-[88vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10" aria-label="close"><X className="w-4 h-4 text-zinc-400" /></button>
        <h3 className="text-2xl font-black font-display text-white mb-4">{t.challenges.create}</h3>
        <form onSubmit={submit} className="space-y-3">
          <input required className="input-base" value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} placeholder={t.challenges.titlePh} data-testid="ch-title" />
          <textarea rows={2} className="input-base" value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} placeholder={t.challenges.descPh} data-testid="ch-desc" />
          <select required className="input-base" value={d.track_id} onChange={(e) => setD({ ...d, track_id: e.target.value })} data-testid="ch-track">
            <option value="" disabled>{t.challenges.pickTrack}</option>
            {tracks.map((tr) => <option key={tr.id} value={tr.id}>{tr.title}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <input required type="number" min="0" step="1" className="input-base" value={d.prize_amount} onChange={(e) => setD({ ...d, prize_amount: e.target.value })} placeholder={t.challenges.prizePh} data-testid="ch-prize" />
            <input required className="input-base" value={d.prize_currency} onChange={(e) => setD({ ...d, prize_currency: e.target.value })} placeholder={t.challenges.currencyPh} data-testid="ch-currency" />
          </div>
          <input required type="number" min="1" max="60" className="input-base" value={d.duration_days} onChange={(e) => setD({ ...d, duration_days: e.target.value })} placeholder={t.challenges.durationPh} data-testid="ch-duration" />
          <FileUploader
            accept="video/mp4,video/webm,video/quicktime,video/*"
            label={t.challenges.uploadAnnounce}
            dataTestid="ch-announce-upload"
            preview={d.announcement_video}
            onUploaded={(url) => setD({ ...d, announcement_video: url })}
            onClear={() => setD({ ...d, announcement_video: "" })}
          />
          <button type="submit" disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="ch-create-btn">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}{t.challenges.launch}
          </button>
        </form>
      </div>
    </div>
  );
}
